import { Link, useRouterState } from "@tanstack/react-router";
import { motion } from "motion/react";
import { CalendarCheck, ClipboardList, Crosshair, LayoutDashboard, Radar, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";
import { haptic } from "@/lib/telegram";
import { useOverlayOpen } from "@/lib/overlay-store";

const ITEMS = [
  { to: "/", label: "Home", icon: LayoutDashboard },
  { to: "/revisions", label: "Revision", icon: CalendarCheck },
  { to: "/questions", label: "Questions", icon: ClipboardList },
  { to: "/tests", label: "Test", icon: Crosshair },
  { to: "/toolkit", label: "Toolkit", icon: Wrench },
  { to: "/tracker-360", label: "Tracker 360", icon: Radar },
] as const;

export function BottomNav() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const overlayOpen = useOverlayOpen();

  return (
    <nav
      aria-hidden={overlayOpen}
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 w-full lg:hidden max-w-full px-3 transition-[opacity,transform] duration-300 [box-sizing:border-box] sm:px-5",
        overlayOpen && "pointer-events-none translate-y-6 opacity-0",
      )}
      style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
    >
      <svg aria-hidden width="0" height="0" className="absolute">
        <defs>
          <linearGradient id="neural-nav-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#5b7cfa">
              <animate
                attributeName="stop-color"
                values="#5b7cfa;#f2545b;#f5c451;#3ec98a;#5b7cfa"
                dur="9s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="100%" stopColor="#3ec98a">
              <animate
                attributeName="stop-color"
                values="#3ec98a;#5b7cfa;#f2545b;#f5c451;#3ec98a"
                dur="9s"
                repeatCount="indefinite"
              />
            </stop>
          </linearGradient>
        </defs>
      </svg>
      <div className="glass mx-auto flex w-full min-w-0 max-w-md items-center lg:max-w-2xl justify-around rounded-3xl px-1 py-2 [box-sizing:border-box] sm:px-2">
        {ITEMS.map((item) => {
          const active =
            item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => haptic("light")}
              className="relative flex min-w-0 flex-1 flex-col items-center gap-1 rounded-2xl px-1 py-2 sm:px-3"
            >
              {active ? (
                <motion.span
                  layoutId="nav-pill"
                  transition={{ type: "spring", stiffness: 320, damping: 28 }}
                  className="absolute inset-0 rounded-2xl bg-primary/15"
                />
              ) : null}
              <item.icon
                className={cn(
                  "relative size-5 transition-colors",
                  active ? "text-primary" : "text-muted-foreground",
                )}
                style={
                  item.to === "/tests"
                    ? {
                        stroke: "url(#neural-nav-gradient)",
                        filter: "drop-shadow(0 0 6px rgba(120,140,255,0.55))",
                      }
                    : undefined
                }
              />
              <span
                className={cn(
                  "relative max-w-full truncate text-[11px] font-medium transition-colors",
                  active ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}